// src/pages/Employees.js
import React, { useState, useEffect } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { employeeService } from '../services/employeeService';
import './Employees.css';

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [newEmployee, setNewEmployee] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'employee',
    language: 'fr'
  });

  // Charger les employés
  const fetchEmployees = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await employeeService.getEmployees();
      setEmployees(data.employees || []);
      setFilteredEmployees(data.employees || []);
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des employés');
      console.error('Erreur fetch employees:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  // Filtrer les employés selon la recherche
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredEmployees(employees);
    } else {
      const filtered = employees.filter(employee =>
        employee.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.employeeId?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredEmployees(filtered);
    }
  }, [searchTerm, employees]);

  // Ajouter un employé
  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      setError('');
      const data = await employeeService.createEmployee(newEmployee);
      
      setEmployees(prev => [data, ...prev]);
      
      if (data.temporaryPassword) {
        setTemporaryPassword(data.temporaryPassword);
        setShowPasswordModal(true);
      }
      
      setNewEmployee({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: 'employee',
        language: 'fr'
      });
      setShowAddForm(false);
    } catch (err) {
      setError(err.message || 'Erreur lors de la création');
      console.error('Erreur create employee:', err);
    }
  };

  // Réinitialiser le mot de passe
  const handleResetPassword = async (employeeId) => {
    const employee = employees.find(emp => emp._id === employeeId);
    
    if (!employee) return;
    
    if (!window.confirm(`Êtes-vous sûr de vouloir réinitialiser le mot de passe de ${employee.firstName} ${employee.lastName} ?\n\nUn nouveau mot de passe sera généré.`)) {
      return;
    }

    try {
      setError('');
      const result = await employeeService.resetPassword(employeeId);
      
      if (result.temporaryPassword) {
        setTemporaryPassword(result.temporaryPassword);
        setShowPasswordModal(true);
      }
    } catch (err) {
      setError(err.message || 'Erreur lors de la réinitialisation du mot de passe');
      console.error('Erreur reset password:', err);
    }
  };

  // Copier dans le presse-papier
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Mot de passe copié dans le presse-papier !');
    }).catch(err => {
      console.error('Erreur lors de la copie:', err);
    });
  };

  // Ouvrir le formulaire de modification
  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setShowEditForm(true);
  };

  // Modifier un employé
  const handleUpdateEmployee = async (e) => {
    e.preventDefault();
    try {
      setError('');
      const updatedEmployee = await employeeService.updateEmployee(
        editingEmployee._id, 
        editingEmployee
      );
      
      setEmployees(prev => 
        prev.map(emp => 
          emp._id === editingEmployee._id ? updatedEmployee : emp
        )
      );
      
      setShowEditForm(false);
      setEditingEmployee(null);
    } catch (err) {
      setError(err.message || 'Erreur lors de la modification');
      console.error('Erreur update employee:', err);
    }
  };

  // Supprimer un employé
  const handleDeleteEmployee = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet employé ?')) {
      return;
    }

    try {
      setError('');
      await employeeService.deleteEmployee(id);
      setEmployees(employees.filter(emp => emp._id !== id));
    } catch (err) {
      setError(err.message || 'Erreur lors de la suppression');
      console.error('Erreur delete employee:', err);
    }
  };

  const getRoleIcon = (role) => {
    switch(role) {
      case 'admin': return '👑';
      case 'manager': return '⭐';
      case 'employee': return '👤';
      default: return '👤';
    }
  };

  const getRoleLabel = (role) => {
    switch(role) {
      case 'admin': return 'Administrateur';
      case 'manager': return 'Manager';
      case 'employee': return 'Employé';
      default: return role;
    }
  };

  const getLanguageLabel = (language) => {
    switch(language) {
      case 'fr': return 'Français';
      case 'en': return 'English';
      case 'ar': return 'العربية';
      default: return language;
    }
  };

  if (loading) {
    return (
      <div className="employees">
        <div className="page-header">
          <div className="page-title">
            <h1>Gestion des Employés</h1>
          </div>
        </div>
        <div className="loading-state">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="employees">
      {/* En-tête */}
      <div className="page-header">
        <div className="page-title">
          <h1>Gestion des Employés</h1>
          <p>Gérez les employés de votre entreprise ({filteredEmployees.length} employé{filteredEmployees.length > 1 ? 's' : ''})</p>
        </div>
        
        <div className="header-actions">
          <Button variant="primary" onClick={() => setShowAddForm(true)}>
            ➕ Ajouter un employé
          </Button>
        </div>
      </div>

      {/* Barre de recherche */}
      <Card className="search-card" padding="md">
        <div className="search-container">
          <div className="search-icon">🔍</div>
          <input
            type="text"
            placeholder="Rechercher un employé par nom, prénom, email ou ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm('')}>✕</button>
          )}
        </div>
      </Card>

      {error && (
        <Card className="error-card" padding="md">
          <div className="error-message">❌ {error}</div>
        </Card>
      )}

      {/* TABLEAU DES EMPLOYÉS */}
      <div className="employees-table-container">
        <table className="employees-table">
          <thead>
            <tr>
              <th>Employé</th>
              <th>ID</th>
              <th>Email</th>
              <th>Téléphone</th>
              <th>Rôle</th>
              <th>Langue</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.map(employee => (
              <tr key={employee._id}>
                <td data-label="Employé">
                  <div className="employee-cell">
                    <strong>{employee.firstName} {employee.lastName}</strong>
                  </div>
                </td>
                <td data-label="ID">
                  <code className="employee-id-code">{employee.employeeId}</code>
                </td>
                <td data-label="Email">
                  <span className="employee-email">{employee.email}</span>
                </td>
                <td data-label="Téléphone">
                  {employee.phone || '-'}
                </td>
                <td data-label="Rôle">
                  <span className={`role-badge-table ${employee.role}`}>
                    {getRoleIcon(employee.role)} {getRoleLabel(employee.role)}
                  </span>
                </td>
                <td data-label="Langue">
                  {getLanguageLabel(employee.language)}
                </td>
                <td data-label="Statut">
                  <span className={`status-badge ${employee.isActive ? 'active' : 'inactive'}`}>
                    {employee.isActive ? '🟢 Actif' : '🔴 Inactif'}
                  </span>
                </td>
                <td data-label="Actions">
                  <div className="actions-cell">
                    <button className="action-btn edit" onClick={() => handleEditEmployee(employee)} title="Modifier">
                      ✏️
                    </button>
                    <button className="action-btn reset" onClick={() => handleResetPassword(employee._id)} title="Réinitialiser MDP">
                      🔄
                    </button>
                    <button className="action-btn delete" onClick={() => handleDeleteEmployee(employee._id)} title="Supprimer">
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredEmployees.length === 0 && !loading && (
        <Card className="empty-state">
          <div className="empty-icon">👥</div>
          <h3>{searchTerm ? 'Aucun employé trouvé' : 'Aucun employé'}</h3>
          <p>{searchTerm ? `Aucun résultat pour "${searchTerm}"` : 'Commencez par ajouter votre premier employé'}</p>
          <Button variant="primary" onClick={() => { setSearchTerm(''); setShowAddForm(true); }}>
            ➕ Ajouter un employé
          </Button>
        </Card>
      )}

      {/* Modal d'affichage du mot de passe */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Mot de passe généré</h2>
              <button className="modal-close" onClick={() => setShowPasswordModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="password-warning">
                <span>⚠️</span>
                <p>Ce mot de passe a été généré pour l'employé — prière de le lui communiquer.</p>
              </div>
              <div className="password-display">
                <label>Mot de passe:</label>
                <div className="password-field">
                  <input type="text" value={temporaryPassword} readOnly className="password-input" />
                  <Button variant="secondary" size="sm" onClick={() => copyToClipboard(temporaryPassword)}>
                    📋 Copier
                  </Button>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <Button variant="primary" onClick={() => setShowPasswordModal(false)}>J'ai noté le mot de passe</Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'ajout */}
      {showAddForm && (
        <div className="modal-overlay" onClick={() => setShowAddForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nouvel Employé</h2>
              <button className="modal-close" onClick={() => setShowAddForm(false)}>✕</button>
            </div>
            <form onSubmit={handleAddEmployee}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Prénom *</label>
                    <input type="text" value={newEmployee.firstName} onChange={(e) => setNewEmployee({...newEmployee, firstName: e.target.value})} required />
                  </div>
                  <div className="form-group">
                    <label>Nom *</label>
                    <input type="text" value={newEmployee.lastName} onChange={(e) => setNewEmployee({...newEmployee, lastName: e.target.value})} required />
                  </div>
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input type="email" value={newEmployee.email} onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>Téléphone *</label>
                  <input type="tel" value={newEmployee.phone} onChange={(e) => setNewEmployee({...newEmployee, phone: e.target.value})} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Rôle *</label>
                    <select value={newEmployee.role} onChange={(e) => setNewEmployee({...newEmployee, role: e.target.value})} required>
                      <option value="employee">👤 Employé</option>
                      <option value="manager">⭐ Manager</option>
                      <option value="admin">👑 Administrateur</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Langue *</label>
                    <select value={newEmployee.language} onChange={(e) => setNewEmployee({...newEmployee, language: e.target.value})} required>
                      <option value="fr">🇫🇷 Français</option>
                      <option value="en">🇬🇧 English</option>
                      <option value="ar">🇩🇿 العربية</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <Button type="button" variant="secondary" onClick={() => setShowAddForm(false)}>Annuler</Button>
                <Button type="submit" variant="primary">Créer l'employé</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de modification */}
      {showEditForm && editingEmployee && (
        <div className="modal-overlay" onClick={() => { setShowEditForm(false); setEditingEmployee(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Modifier l'employé</h2>
              <button className="modal-close" onClick={() => { setShowEditForm(false); setEditingEmployee(null); }}>✕</button>
            </div>
            <form onSubmit={handleUpdateEmployee}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Prénom *</label>
                    <input type="text" value={editingEmployee.firstName} onChange={(e) => setEditingEmployee({...editingEmployee, firstName: e.target.value})} required />
                  </div>
                  <div className="form-group">
                    <label>Nom *</label>
                    <input type="text" value={editingEmployee.lastName} onChange={(e) => setEditingEmployee({...editingEmployee, lastName: e.target.value})} required />
                  </div>
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input type="email" value={editingEmployee.email} onChange={(e) => setEditingEmployee({...editingEmployee, email: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>Téléphone *</label>
                  <input type="tel" value={editingEmployee.phone} onChange={(e) => setEditingEmployee({...editingEmployee, phone: e.target.value})} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Rôle *</label>
                    <select value={editingEmployee.role} onChange={(e) => setEditingEmployee({...editingEmployee, role: e.target.value})} required>
                      <option value="employee">👤 Employé</option>
                      <option value="manager">⭐ Manager</option>
                      <option value="admin">👑 Administrateur</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Langue *</label>
                    <select value={editingEmployee.language} onChange={(e) => setEditingEmployee({...editingEmployee, language: e.target.value})} required>
                      <option value="fr">🇫🇷 Français</option>
                      <option value="en">🇬🇧 English</option>
                      <option value="ar">🇩🇿 العربية</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Statut</label>
                  <select value={editingEmployee.isActive ? 'active' : 'inactive'} onChange={(e) => setEditingEmployee({...editingEmployee, isActive: e.target.value === 'active'})}>
                    <option value="active">🟢 Actif</option>
                    <option value="inactive">🔴 Inactif</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <Button type="button" variant="secondary" onClick={() => { setShowEditForm(false); setEditingEmployee(null); }}>Annuler</Button>
                <Button type="submit" variant="primary">Modifier l'employé</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employees;